import React, { useContext, useEffect, useState } from "react";
import { GlobalContext } from "../../context/globalContext";
import { updateUserAPI } from "../../api/auth";
import AuthTextField from "../../components/Form/AuthTextField";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";

const LOCKED_HINT = "Set at registration and cannot be changed.";

export default function UserPagePersonalDetails() {
  const { currentUser, setCurrentUser } = useContext(GlobalContext);
  const [title, setTitle] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [position, setPosition] = useState("");
  const [country, setCountry] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser) return;
    setTitle(currentUser.title ?? "");
    setOrganisation(currentUser.organisation ?? "");
    setPosition(currentUser.position ?? "");
    setCountry(currentUser.country ?? "");
  }, [currentUser]);

  if (!currentUser) return null;

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);
    setError(null);
    setIsSaving(true);
    try {
      const result = await updateUserAPI({
        title: title.trim(),
        organisation: organisation.trim(),
        position: position.trim(),
        country: country.trim(),
      });
      if (!result.ok) {
        setError(result.message ?? "Failed to save changes.");
        return;
      }
      setCurrentUser(result.user);
      setMessage("Your profile has been updated.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div>
      <h2 className="font-bold text-lg text-slate-900 mb-1">Account</h2>
      <p className="text-sm text-slate-600 mb-6">
        Your profile details used for event registration and receipts.
      </p>

      <form onSubmit={handleSave}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 mb-2">
          <AuthTextField
            label="First name"
            value={currentUser.firstName ?? ""}
            onChange={() => {}}
            readOnly
            hint={LOCKED_HINT}
          />
          <AuthTextField
            label="Last name"
            value={currentUser.lastName ?? ""}
            onChange={() => {}}
            readOnly
            hint={LOCKED_HINT}
          />
        </div>

        <AuthTextField
          label="Email"
          type="email"
          value={currentUser.email}
          onChange={() => {}}
          readOnly
          hint={LOCKED_HINT}
        />

        <div className="border-t border-slate-100 pt-2 mt-2 mb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">
            Editable details
          </p>
          <AuthTextField
            label="Title *"
            value={title}
            onChange={setTitle}
            hint="e.g. Dr, Prof, Mr"
            required
          />
          <AuthTextField
            label="Organisation *"
            value={organisation}
            onChange={setOrganisation}
            autoComplete="organization"
            required
          />
          <AuthTextField
            label="Position *"
            value={position}
            onChange={setPosition}
            required
          />
          <AuthTextField
            label="Country *"
            value={country}
            onChange={setCountry}
            autoComplete="country-name"
            required
          />
        </div>

        {error && <ErrorAlert className="mb-4">{error}</ErrorAlert>}
        {message && (
          <p className="text-sm text-green-700 mb-4">{message}</p>
        )}

        <button
          type="submit"
          className="eh-btn-primary px-6 py-2.5 text-sm font-medium disabled:opacity-60"
          disabled={isSaving}
        >
          {isSaving ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}
